import React, { FC, useContext, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { UiStateContext } from '../../context/UiStateContext';
import RailMapMarker from '../../images/maps/marker-london-rail.png';
import UndergroundMapMarker from '../../images/maps/marker-london-underground.png';
import { Kfh_AreaType, Kfh_StationForAreaGuideType } from '../../types/graphqlTypes';
import Blade from '../Blade';
import { Rail } from '../icon/icons/Rail';
import { Underground } from '../icon/icons/Underground';
import { SideAndMain, StyledSide } from '../layout/SideAndMain';
import { Map, MapMarker } from '../Map';
import { PageBackButton } from '../PageBackButton';
import { Panel } from '../Panel';
import { Section } from '../Section';
import { SEO } from '../Seo';
import { Spacer } from '../Spacer';
import { Typography } from '../Typography';
import { LocationActionSection } from './LocationActionSection';
import { LocationNavigation } from './LocationNavigation';

interface LocationTransportProps {
  location: Kfh_AreaType;
}

interface StationProps {
  name: string;
  lines?: Array<{
    name: string;
  }>;
  zones?: Array<{
    name: string;
  }>;
  type: 'tube' | 'train';
}

interface StyledLineProps {
  name: string;
}

// Styles

const StyledLine = styled(Typography)<StyledLineProps>`
  ${({ theme: { space, global, colors }, name }) => {
    let backgroundColor;
    let textColor;

    switch (name) {
      case 'Piccadilly':
        backgroundColor = '#0019a8';
        textColor = colors.white.default;
        break;
      case 'Victoria':
        backgroundColor = '#00a0e2';
        textColor = colors.white.default;
        break;
      case 'Northern':
        backgroundColor = colors.black.default;
        textColor = colors.white.default;
        break;
      case 'Circle':
        backgroundColor = '#FFCE00';
        textColor = colors.black.default;
        break;
      case 'Hammersmith':
        backgroundColor = '#D799AF';
        textColor = colors.black.default;
        break;
      case 'Metropolitan':
        backgroundColor = '#751056';
        textColor = colors.white.default;
        break;
      case 'Central':
        backgroundColor = colors.brand.default;
        textColor = colors.brand.contrast;
        break;
      case 'District':
        backgroundColor = '#007229';
        textColor = colors.white.default;
        break;
      case 'Waterloo':
        backgroundColor = '#76d0bd';
        textColor = colors.black.default;
        break;
      case 'Jubilee':
        backgroundColor = '#868f98';
        textColor = colors.white.default;
        break;
      case 'zone':
        backgroundColor = colors.grey4.default;
        textColor = colors.grey4.contrast;
        break;
      default:
        backgroundColor = colors.grey4.default;
        textColor = colors.grey4.contrast;
    }

    return css`
      background-color: ${backgroundColor};
      color: ${textColor};
      border-radius: ${global.borderRadius.default};
      padding: ${space.sm};

      & + & {
        margin-top: ${space.xxs};
      }
    `;
  }}
`;

const StyledStationGrid = styled.ul`
  ${({ theme: { space, media } }) => css`
    display: grid;
    gap: ${space.md};

    // Tablet > styles

    @media (min-width: ${media.md}) {
      gap: ${space.lg};
      grid-template-columns: repeat(2, 1fr);
    }

    // Desktop > styles

    @media (min-width: ${media.xl}) {
      grid-template-columns: repeat(3, 1fr);
    }
  `};
`;

const StyledStationHeading = styled(Typography)`
  ${({ theme: { space } }) => css`
    margin-bottom: ${space.md};
    margin-top: ${space.md};
  `};
`;

// JSX

const Station: FC<StationProps> = ({ name, lines, zones, type }) => (
  <Panel backgroundColor="grey1" as="li">
    {type === 'tube' ? <Underground size="default" /> : <Rail size="default" />}
    <StyledStationHeading component="h3" variant="titleMedium">
      {name}
    </StyledStationHeading>
    {(lines && lines.length > 0) || (zones && zones.length > 0) ? (
      <ul>
        {lines && lines.length > 0 && (
          <>
            {lines
              ?.filter((line) => !!line?.name)
              .map((line) => (
                <StyledLine component="li" variant="caps" name={line?.name}>
                  {line?.name}
                </StyledLine>
              ))}
          </>
        )}
        {zones && zones.length > 0 && (
          <>
            {zones
              ?.filter((zone) => !!zone?.name)
              .map((zone) => (
                <StyledLine component="li" variant="caps" name="zone">
                  Zone {zone.name}
                </StyledLine>
              ))}
          </>
        )}
      </ul>
    ) : (
      ''
    )}
  </Panel>
);

const railIconComponent = RailMapMarker;
const tubeIconComponent = UndergroundMapMarker;

const LocationTransport: FC<LocationTransportProps> = ({ location }) => {
  const [activeStation, setActiveStation] = useState<Kfh_StationForAreaGuideType>();
  const { setActiveBlade } = useContext(UiStateContext);
  const { name, relativeUrl, stations, seoPages, coordinates } = location;
  const seo = seoPages.find((seoPage) => seoPage.remoteTypeName === 'KfhSeoAreaGuideTransportType');
  const tubeStations = stations?.filter((station) => station.code === 'tube');
  const nationalRailStations = stations?.filter((station) => station.code === 'train');

  // Map markers

  const mapMarkers: MapMarker[] = useMemo(() => {
    const handleOpenBlade = (stationToShow: Kfh_StationForAreaGuideType) => {
      setActiveStation(stationToShow);
      setActiveBlade('transport');
    };

    const locationMapMarkers = stations.map((station) => ({
      position: {
        lat: station.latitude,
        lng: station.longitude,
      },
      id: station.id,
      onClick: () => handleOpenBlade(station),
      iconComponent: station.code === 'tube' ? tubeIconComponent : railIconComponent,
    }));

    return locationMapMarkers;
  }, [stations, setActiveBlade]);

  const defaultMultisearch = location.artirixMultiSearch
    ? {
        name: location.artirixDisplayName,
        multiSearch: location.artirixMultiSearch,
      }
    : {};
  return (
    <>
      {/* Back button */}

      <PageBackButton url={relativeUrl} label={`${name} overview`} />

      <Section contained>
        <SideAndMain>
          {/* Main content */}

          <Spacer responsive>
            {/* Heading */}

            <Typography component="h1" variant="displaySmall">
              {seo?.h1 ? seo.h1 : `${name} transport`}
            </Typography>

            {/* Map */}

            <Map markers={mapMarkers} mapStyle="transport" polygon={coordinates} />

            {/* Transport - Tube stations */}

            {tubeStations && tubeStations.length > 0 && (
              <Spacer>
                <Typography component="h2" variant="titleMedium">
                  Tube stations
                </Typography>
                <StyledStationGrid>
                  {tubeStations?.map(({ name: stationName, lines, zones }) => (
                    <Station lines={lines} zones={zones} name={stationName} type="tube" />
                  ))}
                </StyledStationGrid>
              </Spacer>
            )}

            {/* Transport - National Rail stations */}
            {nationalRailStations && nationalRailStations.length > 0 && (
              <Spacer>
                <Typography component="h2" variant="titleMedium">
                  National rail
                </Typography>
                <StyledStationGrid>
                  {nationalRailStations?.map(({ name: stationName, zones }) => (
                    <Station name={stationName} zones={zones} type="train" />
                  ))}
                </StyledStationGrid>
              </Spacer>
            )}
          </Spacer>

          {/* Side navigation */}
          <StyledSide>
            <LocationNavigation url={relativeUrl} name={name} isArea />
          </StyledSide>
        </SideAndMain>
      </Section>

      {/* Actions */}
      <LocationActionSection defaultMultisearch={defaultMultisearch} />

      {/* Transport Blade */}
      {activeStation && (
        <Blade id="transport">
          <Station
            lines={activeStation.code === 'tube' ? activeStation.lines : []}
            zones={activeStation.zones}
            name={activeStation.name}
            type={activeStation.code}
          />
        </Blade>
      )}
    </>
  );
};

export default LocationTransport;
